import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './LendBorrow.css';
import { useAllTransactions, useAccounts, QUERY_KEYS } from '../TransactionService';
import { useQueryClient } from '@tanstack/react-query';
import api from '../api';

// Heuristic prefix the form uses when storing peer-to-peer entries.
// Lent / Borrowed / Settled rows all start with one of these so we can
// reconstruct per-person balances by parsing the description on read.
const DESC_PATTERN = /^(Lent to|Borrowed from|Settled with) ([^:]+?)(?:\s*:\s*(.*))?$/i;

// Money helper — Indian-style grouping fits the ₹ formatting elsewhere.
const fmt = (n) => Number(n || 0).toLocaleString();

const todayStr = () => new Date().toISOString().slice(0, 10);

// Helper: compute how many monthly EMIs have elapsed since start_date up to today.
// Used to show accurate progress when the backend hasn't processed catch-up yet.
const inferredPaidCount = (sched) => {
  const stored = sched.occurrences_count || 0;
  if (stored > 0) return { count: stored, inferred: false };

  // occurrences_count is 0 — calculate from start_date if it's in the past
  const start = sched.start_date ? new Date(sched.start_date) : null;
  if (!start || isNaN(start.getTime())) return { count: 0, inferred: false };

  const today = new Date(); today.setHours(0, 0, 0, 0);
  if (start >= today) return { count: 0, inferred: false };

  const dueDay = sched.recurrence_day_of_month || start.getDate();
  let count = 0;
  const cursor = new Date(start.getFullYear(), start.getMonth(), dueDay);
  while (cursor <= today) {
    count++;
    cursor.setMonth(cursor.getMonth() + 1);
  }
  const maxCount = sched.max_occurrences || null;
  if (maxCount !== null) count = Math.min(count, maxCount);
  return { count, inferred: count > 0 };
};

// Helper: parse bank-confirmed outstanding from description [bal:XXXXX]
const parseBalFromDesc = (desc) => {
  const m = (desc || '').match(/\[bal:([\d.]+)\]/);
  return m ? parseFloat(m[1]) : null;
};

// Helper: strip display metadata from description for clean label
const cleanEmiName = (desc) =>
  (desc || '')
    .replace(/\s*\[bal:[\d.]+\]/, '')
    .trim();

const EmiProgress = ({ emi, variant = 'full' }) => {
  const perEmi = parseFloat(emi.amount) || 0;
  const { count: paidCount, inferred } = inferredPaidCount(emi);
  const totalCount = emi.max_occurrences || null;

  const paidAmt = perEmi * paidCount;
  const totalAmt = totalCount ? perEmi * totalCount : null;
  const calcRemainingAmt = totalAmt !== null ? Math.max(0, totalAmt - paidAmt) : null;
  const bankRemainingAmt = parseBalFromDesc(emi.description);  // null if not set

  // Which remaining figure to trust: bank-confirmed wins if available
  const displayRemainingAmt = bankRemainingAmt !== null ? bankRemainingAmt : calcRemainingAmt;
  const adjustmentAmt = (bankRemainingAmt !== null && calcRemainingAmt !== null)
    ? bankRemainingAmt - calcRemainingAmt
    : null;

  // Open-ended EMI: no progress bar (no denominator), just show what's paid.
  if (totalAmt === null) {
    return (
      <div className={`lb-emi-progress ${variant}`}>
        <div className="lb-emi-progress-meta">
          <span className="lb-emi-progress-stat">
            <span className="lb-emi-progress-num">₹{fmt(paidAmt)}</span>
            <span className="lb-emi-progress-lbl">paid · {paidCount} EMIs{inferred ? ' (est.)' : ''}</span>
          </span>
          <span className="lb-emi-progress-tag open">Open-ended</span>
        </div>
      </div>
    );
  }

  const remainingCount = Math.max(0, totalCount - paidCount);
  // For percentage, use bank outstanding if available so bar reflects reality
  const pct = totalAmt > 0
    ? Math.min(100, ((totalAmt - (displayRemainingAmt ?? calcRemainingAmt)) / totalAmt) * 100)
    : 0;

  return (
    <div className={`lb-emi-progress ${variant}`}>
      <div className="lb-emi-progress-bar">
        <div className="lb-emi-progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="lb-emi-progress-meta">
        <span className="lb-emi-progress-stat paid">
          <span className="lb-emi-progress-num">₹{fmt(paidAmt)}</span>
          <span className="lb-emi-progress-lbl">
            paid · {paidCount}/{totalCount}{inferred ? ' (est.)' : ''}
          </span>
        </span>
        <span className="lb-emi-progress-stat remaining">
          <span className="lb-emi-progress-num">₹{fmt(displayRemainingAmt)}</span>
          <span className="lb-emi-progress-lbl">
            left · {remainingCount} EMIs
            {bankRemainingAmt !== null && <span className="lb-emi-bank-tag"> · bank</span>}
          </span>
        </span>
        <span className="lb-emi-progress-stat total">
          <span className="lb-emi-progress-num">₹{fmt(totalAmt)}</span>
          <span className="lb-emi-progress-lbl">total · {Math.round(pct)}%</span>
        </span>
      </div>
      {adjustmentAmt !== null && Math.abs(adjustmentAmt) > 1 && (
        <div className={`lb-emi-reconcile ${adjustmentAmt < 0 ? 'ahead' : 'behind'}`}>
          <div className="lb-emi-reconcile-row">
            <span className="lb-emi-reconcile-item">
              <span className="lb-emi-reconcile-lbl">🏦 Bank says</span>
              <span className="lb-emi-reconcile-val">₹{fmt(bankRemainingAmt)}</span>
            </span>
            <span className="lb-emi-reconcile-sep">vs</span>
            <span className="lb-emi-reconcile-item">
              <span className="lb-emi-reconcile-lbl">🧮 Calculated</span>
              <span className="lb-emi-reconcile-val">₹{fmt(calcRemainingAmt)}</span>
            </span>
            <span className="lb-emi-reconcile-diff">
              {adjustmentAmt < 0
                ? `✅ ₹${fmt(-adjustmentAmt)} ahead`
                : `⚠️ ₹${fmt(adjustmentAmt)} gap`}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const LendBorrow = ({ dashboardId }) => {
  const queryClient = useQueryClient();
  const { data: transactionsRaw } = useAllTransactions(dashboardId);
  const { data: accountsRaw } = useAccounts(dashboardId);

  const [activeTab, setActiveTab] = useState('overview'); // overview | lend | borrow | loans
  const [submitting, setSubmitting] = useState(false);

  // Lend/Borrow form
  const [personName, setPersonName] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [accountId, setAccountId] = useState('');
  const [txDate, setTxDate] = useState(todayStr());

  // EMI form
  const [emiSchedules, setEmiSchedules] = useState([]);
  const [emiProvider, setEmiProvider] = useState('');
  const [emiAmount, setEmiAmount] = useState('');
  const [emiAccount, setEmiAccount] = useState('');
  const [emiStartDate, setEmiStartDate] = useState(todayStr());
  const [emiDueDay, setEmiDueDay] = useState(5);
  const [emiTenure, setEmiTenure] = useState('');
  const [emiInterest, setEmiInterest] = useState('');
  const [emiSubmitting, setEmiSubmitting] = useState(false);
  const [editingEmiId, setEditingEmiId] = useState(null);
  const [showEmiForm, setShowEmiForm] = useState(false);
  const [emiOutstanding, setEmiOutstanding] = useState(''); // bank-confirmed outstanding

  const transactions = useMemo(() => transactionsRaw || [], [transactionsRaw]);
  const accounts = useMemo(() => accountsRaw || [], [accountsRaw]);

  // Auto-pick first account once available
  useEffect(() => {
    if (accounts.length > 0) {
      if (!accountId) setAccountId(accounts[0].id);
      if (!emiAccount) setEmiAccount(accounts[0].id);
    }
  }, [accounts, accountId, emiAccount]);

  // ── EMI schedule fetch ─────────────────────────────────────────
  const fetchEmiSchedules = useCallback(async () => {
    if (!dashboardId) return;
    try {
      const { data } = await api.get(`/api/scheduled-transactions?dashboard_id=${dashboardId}`);
      setEmiSchedules((data || []).filter(s => s.category === 'emi'));
    } catch (err) {
      console.warn('[LendBorrow] failed to load EMI schedules', err);
    }
  }, [dashboardId]);

  useEffect(() => { fetchEmiSchedules(); }, [fetchEmiSchedules]);

  // Auto-run catch-up silently on mount if any EMI's next_run_date is overdue.
  // This ensures occurrences_count stays in sync so progress bars show real data.

  // ── Derived: loan-related transactions ────────────────────────
  const loanTxs = useMemo(() => transactions.filter(tx =>
    tx.category === 'debt' ||
    tx.category === 'emi' ||
    DESC_PATTERN.test(tx.description || '')
  ), [transactions]);

  // ── Per-person open balances ──────────────────────────────────
  // Net amount the person owes you. Positive = receivable, negative = payable.
  // Settled rows zero out the balance, which is exactly what we want.
  const personBalances = useMemo(() => {
    const map = new Map();
    for (const tx of loanTxs) {
      const m = (tx.description || '').match(DESC_PATTERN);
      if (!m) continue;
      const action = m[1].toLowerCase();
      const name = m[2].trim();
      if (!name) continue;

      const key = name.toLowerCase();
      const entry = map.get(key) || { name, lent: 0, borrowed: 0, settled: 0, lastDate: '' };
      const amt = Math.abs(parseFloat(tx.amount) || 0);

      if (action.startsWith('lent')) entry.lent += amt;
      else if (action.startsWith('borrowed')) entry.borrowed += amt;
      else if (action.startsWith('settled')) {
        // A settle row balances out — direction comes from transaction_type:
        //   credit (we received money back) → reduces what they owe us
        //   debit  (we paid them back)      → reduces what we owe them
        if (tx.transaction_type === 'credit') entry.lent -= amt;
        else entry.borrowed -= amt;
        entry.settled += amt;
      }
      if (!entry.lastDate || (tx.transaction_date || '') > entry.lastDate) {
        entry.lastDate = tx.transaction_date || entry.lastDate;
      }
      map.set(key, entry);
    }
    return Array.from(map.values())
      .map(e => ({ ...e, net: e.lent - e.borrowed }))
      .filter(e => Math.abs(e.net) > 0.005)
      .sort((a, b) => Math.abs(b.net) - Math.abs(a.net));
  }, [loanTxs]);

  // ── Aggregate stats from ACTIVE balances (settlements have already
  //    been subtracted) so the headline numbers reflect what's still open.
  const stats = useMemo(() => {
    let lent = 0, borrowed = 0;
    for (const p of personBalances) {
      if (p.net > 0) lent += p.net;
      else borrowed += -p.net;
    }
    return { lent, borrowed };
  }, [personBalances]);

  const net = stats.lent - stats.borrowed;

  // ── Upcoming EMI reminders ─────────────────────────────────────
  const upcomingEmis = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return emiSchedules
      .filter(s => s.is_active !== false)
      .map(s => {
        const due = s.next_run_date ? new Date(s.next_run_date) : null;
        const daysAway = due ? Math.round((due - today) / 86400000) : null;
        return { ...s, daysAway };
      })
      .filter(s => s.daysAway !== null)
      .sort((a, b) => a.daysAway - b.daysAway)
      .slice(0, 5);
  }, [emiSchedules]);

  // ── Recent activity (top 10, newest first) ─────────────────────
  const recentActivity = useMemo(() => {
    return [...loanTxs]
      .sort((a, b) => (b.transaction_date || '').localeCompare(a.transaction_date || ''))
      .slice(0, 10);
  }, [loanTxs]);

  // ── Mutation helpers ─────────────────────────────────────────
  const refreshTxCaches = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allTransactions(dashboardId) });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accounts(dashboardId) });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dashboardSummary(dashboardId) });
  };

  // ── Lend / Borrow record ────────────────────────────────────
  const handleSubmit = async (type) => {
    if (!personName.trim() || !amount || parseFloat(amount) <= 0) {
      alert('Please fill in person name and a positive amount');
      return;
    }
    if (!accountId) {
      alert('Please select an account');
      return;
    }
    setSubmitting(true);
    try {
      const desc = `${type === 'lend' ? 'Lent to' : 'Borrowed from'} ${personName.trim()}`;
      const fullDesc = description.trim() ? `${desc}: ${description.trim()}` : desc;
      await api.post('/api/transactions', {
        dashboard_id: dashboardId,
        account_id: accountId,
        amount: parseFloat(amount),
        transaction_type: type === 'lend' ? 'debit' : 'credit',
        category: 'debt',
        description: fullDesc,
        transaction_date: txDate,
      });

      setPersonName('');
      setAmount('');
      setDescription('');
      setTxDate(todayStr());
      refreshTxCaches();
      setActiveTab('overview');
    } catch (err) {
      alert('Failed to record transaction');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Settle a person ───────────────────────────────────────────
  const handleSettle = async (person) => {
    if (!accountId) {
      alert('Please open Lend or Borrow once to pick a default account, then try again.');
      return;
    }
    const confirmText = person.net > 0
      ? `Mark ₹${fmt(person.net)} from ${person.name} as received?`
      : `Mark ₹${fmt(-person.net)} paid to ${person.name}?`;
    if (!window.confirm(confirmText)) return;

    try {
      await api.post('/api/transactions', {
        dashboard_id: dashboardId,
        account_id: accountId,
        amount: Math.abs(person.net),
        // net > 0: they owed us → settling means money came IN (credit).
        // net < 0: we owed them → settling means money goes OUT (debit).
        transaction_type: person.net > 0 ? 'credit' : 'debit',
        category: 'debt',
        description: `Settled with ${person.name}`,
        transaction_date: todayStr(),
      });
      refreshTxCaches();
    } catch (err) {
      alert('Failed to record settlement');
    }
  };

  // ── EMI form handlers ───────────────────────────────────────
  const resetEmiForm = () => {
    setEmiProvider('');
    setEmiAmount('');
    setEmiStartDate(todayStr());
    setEmiDueDay(5);
    setEmiTenure('');
    setEmiInterest('');
    setEmiOutstanding('');
    setEditingEmiId(null);
    setShowEmiForm(false);
  };

  const handleEmiSubmit = async () => {
    if (!emiProvider.trim() || !emiAmount || parseFloat(emiAmount) <= 0) {
      alert('Provider name and EMI amount are required');
      return;
    }
    if (!emiAccount) {
      alert('Please select an account');
      return;
    }

    setEmiSubmitting(true);
    try {
      const interestNote = emiInterest ? ` @ ${emiInterest}%` : '';
      const balNote = emiOutstanding ? ` [bal:${parseFloat(emiOutstanding).toFixed(0)}]` : '';
      const payload = {
        dashboard_id: dashboardId,
        account_id: emiAccount,
        amount: parseFloat(emiAmount),
        transaction_type: 'debit',
        category: 'emi',
        description: `${emiProvider.trim()}${interestNote}${balNote}`,
        recurrence_type: 'monthly_fixed',
        recurrence_day_of_month: parseInt(emiDueDay, 10) || 1,
        start_date: emiStartDate,
        max_occurrences: emiTenure ? parseInt(emiTenure, 10) : null,
        run_time: '09:00',
      };

      if (editingEmiId) {
        await api.patch(`/api/scheduled-transactions/${editingEmiId}`, payload);
      } else {
        await api.post('/api/scheduled-transactions', payload);
        // No backfill — transactions will be recorded from the next due date onwards.
      }

      resetEmiForm();
      fetchEmiSchedules();
    } catch (err) {
      alert('Failed to save EMI');
    } finally {
      setEmiSubmitting(false);
    }
  };

  const handleEditEmi = (sched) => {
    // Strip both [bal:...] and interest suffix from provider name
    const rawDesc = sched.description || '';
    const mBal = rawDesc.match(/\[bal:([\d.]+)\]/);
    const mInt = rawDesc.match(/@\s*([\d.]+)%/);
    const cleanName = rawDesc
      .replace(/\s*\[bal:[\d.]+\]/, '')
      .replace(/\s*@\s*[\d.]+%/, '')
      .trim();
    setEmiProvider(cleanName);
    setEmiAmount(String(sched.amount));
    setEmiAccount(sched.account_id);
    setEmiStartDate(sched.start_date);
    setEmiDueDay(sched.recurrence_day_of_month || 5);
    setEmiTenure(sched.max_occurrences || '');
    setEmiInterest(mInt ? mInt[1] : '');
    setEmiOutstanding(mBal ? mBal[1] : '');
    setEditingEmiId(sched.id);
    setShowEmiForm(true);
  };

  const handleEmiRunNow = async (id) => {
    try {
      await api.post(`/api/scheduled-transactions/${id}/run-now`);
      fetchEmiSchedules();
      refreshTxCaches();
    } catch (err) {
      alert('Failed to run EMI');
    }
  };

  const handleEmiToggleActive = async (sched) => {
    try {
      await api.patch(`/api/scheduled-transactions/${sched.id}`, {
        is_active: !sched.is_active,
      });
      fetchEmiSchedules();
    } catch (err) {
      alert('Failed to update EMI');
    }
  };

  const handleEmiDelete = async (id) => {
    if (!window.confirm('Delete this EMI schedule? Past payments will remain in your transaction history.')) return;
    try {
      await api.delete(`/api/scheduled-transactions/${id}`);
      fetchEmiSchedules();
    } catch (err) {
      alert('Failed to delete EMI');
    }
  };

  // Count EMIs whose next_run_date is in the past — these are candidates
  // for the dashboard-wide /process catch-up call.
  const overdueEmiCount = useMemo(() => {
    const today = todayStr();
    return emiSchedules.filter(s =>
      s.is_active !== false &&
      s.next_run_date &&
      s.next_run_date <= today
    ).length;
  }, [emiSchedules]);

  const [catchingUp, setCatchingUp] = useState(false);

  const handleCatchUp = async () => {
    if (overdueEmiCount === 0) return;
    if (!window.confirm(
      `Auto-record every missed EMI/SIP up to today? ` +
      `(${overdueEmiCount} schedule${overdueEmiCount === 1 ? '' : 's'} overdue.)`
    )) return;
    setCatchingUp(true);
    try {
      // Snapshot occurrence counts BEFORE the call so we can verify the
      // backend actually advanced them — catching the silent "200 OK but
      // processed 0" case where the schedule didn't match the query.
      const beforeMap = new Map(
        emiSchedules.map(s => [s.id, s.occurrences_count || 0])
      );

      const { data } = await api.post(
        `/api/scheduled-transactions/process?dashboard_id=${dashboardId}`
      );
      const processed = data?.processed ?? 0;

      // Re-fetch and inspect the deltas.
      const { data: fresh } = await api.get(
        `/api/scheduled-transactions?dashboard_id=${dashboardId}`
      );
      const freshEmis = (fresh || []).filter(s => s.category === 'emi');
      setEmiSchedules(freshEmis);
      refreshTxCaches();

      const advanced = freshEmis.filter(
        s => (s.occurrences_count || 0) > (beforeMap.get(s.id) || 0)
      );
      const totalNewPayments = advanced.reduce(
        (n, s) => n + ((s.occurrences_count || 0) - (beforeMap.get(s.id) || 0)),
        0
      );

      if (totalNewPayments > 0) {
        alert(
          `Recorded ${totalNewPayments} past payment${totalNewPayments === 1 ? '' : 's'} ` +
          `across ${advanced.length} schedule${advanced.length === 1 ? '' : 's'}.`
        );
      } else if (processed > 0) {
        alert(
          `Backend processed ${processed} schedule${processed === 1 ? '' : 's'} ` +
          `but no occurrence counts moved. They may have hit their tenure limit ` +
          `or been deactivated. Check the schedule's is_active flag.`
        );
      } else {
        alert(
          `Backend reports 0 schedules matched. Likely cause: the schedule's ` +
          `is_active is false, or its dashboard_id doesn't match the current dashboard.`
        );
      }
    } catch (err) {
      console.error('[LendBorrow] catch-up failed', err);
      alert(
        `Failed to catch up scheduled EMIs.\n\n` +
        `Status: ${err?.response?.status || 'no response'}\n` +
        `Detail: ${err?.response?.data?.detail || err?.message || 'unknown'}`
      );
    } finally {
      setCatchingUp(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div className="lb-container">
      <div className="lb-header">
        <span className="lb-header-badge">🤝 Debt Tracker</span>
        <h1 className="lb-header-title">Lend &amp; Borrow</h1>
        <p className="lb-header-sub">
          Track loans, debts, EMIs and shared IOUs. See who owes you, who you owe,
          and what's coming up next.
        </p>
      </div>

      <div className="lb-stats-row">
        <div className="lb-stat-card lent">
          <span className="lb-stat-label">You're Owed</span>
          <span className="lb-stat-value">₹{fmt(stats.lent)}</span>
        </div>
        <div className="lb-stat-card borrowed">
          <span className="lb-stat-label">You Owe</span>
          <span className="lb-stat-value">₹{fmt(stats.borrowed)}</span>
        </div>
        <div className="lb-stat-card net">
          <span className="lb-stat-label">Net Balance</span>
          <span className={`lb-stat-value ${net >= 0 ? 'pos' : 'neg'}`}>
            ₹{fmt(Math.abs(net))}
            <small>{net >= 0 ? 'Receivable' : 'Payable'}</small>
          </span>
        </div>
      </div>

      <div className="lb-nav">
        {[
          { id: 'overview', label: 'Overview',     icon: '📊' },
          { id: 'lend',     label: 'Lend Money',   icon: '📤' },
          { id: 'borrow',   label: 'Borrow Money', icon: '📥' },
          { id: 'loans',    label: 'Loan / EMI',   icon: '🏦' },
        ].map(tab => (
          <button
            key={tab.id}
            className={`lb-nav-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="lb-nav-icon">{tab.icon}</span>
            <span className="lb-nav-label">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="lb-content-grid">

        {/* ───────── OVERVIEW ───────── */}
        {activeTab === 'overview' && (
          <>
            <div className="lb-panel">
              <div className="lb-panel-hd">
                <h3>Open Balances</h3>
                <span className="lb-panel-sub">
                  {personBalances.length === 0
                    ? 'Nothing outstanding — all settled up.'
                    : 'Tap "Settle" once an entry is paid off.'}
                </span>
              </div>
              {personBalances.length > 0 ? (
                <div className="lb-people-list">
                  {personBalances.map(p => (
                    <div key={p.name} className="lb-person-row">
                      <div className="lb-person-main">
                        <span className={`lb-person-avatar ${p.net > 0 ? 'lent' : 'borrow'}`}>
                          {p.name.charAt(0).toUpperCase()}
                        </span>
                        <div className="lb-person-info">
                          <span className="lb-person-name">{p.name}</span>
                          <span className="lb-person-meta">
                            {p.net > 0 ? 'owes you' : 'you owe'}
                            {p.lastDate && ` · last activity ${new Date(p.lastDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`}
                          </span>
                        </div>
                      </div>
                      <div className="lb-person-actions">
                        <span className={`lb-person-amt ${p.net > 0 ? 'lent' : 'borrow'}`}>
                          ₹{fmt(Math.abs(p.net))}
                        </span>
                        <button className="lb-settle-btn" onClick={() => handleSettle(p)}>
                          Settle
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="lb-empty-state">
                  <p>You're all settled. Use the Lend or Borrow tabs to record a new entry.</p>
                </div>
              )}
            </div>

            <div className="lb-panel">
              <div className="lb-panel-hd">
                <h3>Upcoming EMIs</h3>
                <span className="lb-panel-sub">Next scheduled deductions</span>
              </div>
              {upcomingEmis.length > 0 ? (
                <div className="lb-reminders-list">
                  {upcomingEmis.map(emi => {
                    const acct = accounts.find(a => a.id === emi.account_id);
                    const dueLabel =
                      emi.daysAway < 0 ? `${Math.abs(emi.daysAway)}d overdue` :
                      emi.daysAway === 0 ? 'Due today' :
                      emi.daysAway === 1 ? 'Tomorrow' :
                      `In ${emi.daysAway} days`;
                    const flag = emi.daysAway < 0 ? 'overdue' : emi.daysAway <= 3 ? 'soon' : 'later';
                    return (
                      <div key={emi.id} className="lb-reminder-row">
                        <div className="lb-reminder-top">
                          <div className="lb-reminder-main">
                            <span className={`lb-reminder-flag ${flag}`}>{dueLabel}</span>
                            <div className="lb-reminder-info">
                              <span className="lb-reminder-name">{emi.description || 'EMI'}</span>
                              <span className="lb-reminder-meta">
                                {acct ? acct.name : 'Account'} · {emi.next_run_date}
                              </span>
                            </div>
                          </div>
                          <span className="lb-reminder-amt">₹{fmt(emi.amount)}</span>
                        </div>
                        <EmiProgress emi={emi} variant="compact" />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="lb-empty-state">
                  <p>No upcoming EMIs. Add one from the Loan / EMI tab.</p>
                </div>
              )}
            </div>

            <div className="lb-panel lb-full-width">
              <div className="lb-panel-hd">
                <h3>Recent Activity</h3>
                <span className="lb-panel-sub">Last {recentActivity.length} loan / debt transactions</span>
              </div>
              {recentActivity.length > 0 ? (
                <div className="lb-history-list">
                  {recentActivity.map(tx => {
                    const isLend = tx.transaction_type === 'debit' && /^Lent to/i.test(tx.description || '');
                    const isBorrow = tx.transaction_type === 'credit' && /^Borrowed from/i.test(tx.description || '');
                    const isSettle = /^Settled with/i.test(tx.description || '');
                    const tone = isSettle ? 'settle' : (isLend || tx.transaction_type === 'debit') ? 'lend' : 'borrow';
                    const arrow = isSettle ? '✓' : tone === 'lend' ? '↗' : '↘';
                    return (
                      <div key={tx.id} className="lb-history-row">
                        <div className="lb-hist-main">
                          <span className={`lb-hist-icon ${tone}`}>{arrow}</span>
                          <div className="lb-hist-info">
                            <span className="lb-hist-desc">{tx.description || tx.category}</span>
                            <span className="lb-hist-date">
                              {new Date(tx.transaction_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          </div>
                        </div>
                        <span className={`lb-hist-amt ${tone}`}>
                          ₹{fmt(parseFloat(tx.amount))}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="lb-empty-state">
                  <p>No recent activity yet.</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* ───────── LEND / BORROW FORM ───────── */}
        {(activeTab === 'lend' || activeTab === 'borrow') && (
          <div className="lb-panel lb-full-width">
            <div className="lb-panel-hd">
              <h3>{activeTab === 'lend' ? 'Lend Money' : 'Borrow Money'}</h3>
              <span className="lb-panel-sub">
                Record a new {activeTab === 'lend' ? 'loan given' : 'debt taken'}
              </span>
            </div>
            <div className="lb-form">
              <div className="lb-form-grid">
                <div className="lb-input-group">
                  <label>Person Name</label>
                  <input
                    type="text"
                    placeholder="Who are you dealing with?"
                    value={personName}
                    onChange={e => setPersonName(e.target.value)}
                  />
                </div>
                <div className="lb-input-group">
                  <label>Amount</label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                  />
                </div>
                <div className="lb-input-group">
                  <label>Account</label>
                  <select value={accountId} onChange={e => setAccountId(e.target.value)}>
                    {accounts.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
                  </select>
                </div>
                <div className="lb-input-group">
                  <label>Date</label>
                  <input type="date" value={txDate} onChange={e => setTxDate(e.target.value)} />
                </div>
              </div>
              <div className="lb-input-group full">
                <label>Note / Description (Optional)</label>
                <textarea
                  placeholder="What's it for?"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={2}
                />
              </div>
              <button
                className={`lb-submit-btn ${activeTab}`}
                onClick={() => handleSubmit(activeTab)}
                disabled={submitting}
              >
                {submitting ? 'Recording...' : `Confirm ${activeTab === 'lend' ? 'Lending' : 'Borrowing'}`}
              </button>
            </div>
          </div>
        )}

        {/* ───────── LOAN / EMI ───────── */}
        {activeTab === 'loans' && (
          <div className="lb-panel lb-full-width">
            <div className="lb-panel-hd">
              <h3>Loans &amp; EMIs</h3>
              <span className="lb-panel-sub">
                Bank loans, credit-card EMIs and any recurring repayment.
              </span>
            </div>

            {showEmiForm ? (
              <div className="lb-form">
                <div className="lb-form-grid">
                  <div className="lb-input-group">
                    <label>Lender / Provider</label>
                    <input
                      type="text"
                      placeholder="e.g. HDFC Personal Loan"
                      value={emiProvider}
                      onChange={e => setEmiProvider(e.target.value)}
                    />
                  </div>
                  <div className="lb-input-group">
                    <label>EMI Amount</label>
                    <input
                      type="number"
                      placeholder="0.00"
                      value={emiAmount}
                      onChange={e => setEmiAmount(e.target.value)}
                    />
                  </div>
                  <div className="lb-input-group">
                    <label>Debit From</label>
                    <select value={emiAccount} onChange={e => setEmiAccount(e.target.value)}>
                      {accounts.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
                    </select>
                  </div>
                  <div className="lb-input-group">
                    <label>Day of Month</label>
                    <input
                      type="number" min="1" max="31"
                      value={emiDueDay}
                      onChange={e => setEmiDueDay(e.target.value)}
                    />
                  </div>
                  <div className="lb-input-group">
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={emiStartDate}
                      onChange={e => setEmiStartDate(e.target.value)}
                    />
                  </div>
                  <div className="lb-input-group">
                    <label>Total EMIs (Tenure)</label>
                    <input
                      type="number" min="1"
                      placeholder="e.g. 60"
                      value={emiTenure}
                      onChange={e => setEmiTenure(e.target.value)}
                    />
                  </div>
                  <div className="lb-input-group">
                    <label>Interest Rate % (optional)</label>
                    <input
                      type="number" step="0.01" min="0"
                      placeholder="e.g. 10.5"
                      value={emiInterest}
                      onChange={e => setEmiInterest(e.target.value)}
                    />
                  </div>
                  <div className="lb-input-group">
                    <label>
                      Current Outstanding Balance (from bank)
                      <span style={{fontSize:'11px',fontWeight:400,color:'#64748b',marginLeft:6}}>optional — overrides calculated remaining</span>
                    </label>
                    <input
                      type="number" min="0" step="1"
                      placeholder="e.g. 150199"
                      value={emiOutstanding}
                      onChange={e => setEmiOutstanding(e.target.value)}
                    />
                  </div>
                </div>
                <div className="lb-form-actions">
                  <button
                    className="lb-submit-btn lend"
                    onClick={handleEmiSubmit}
                    disabled={emiSubmitting}
                  >
                    {emiSubmitting ? 'Saving...' : (editingEmiId ? 'Save Changes' : 'Add EMI')}
                  </button>
                  <button className="lb-cancel-btn" onClick={resetEmiForm}>Cancel</button>
                </div>
              </div>
            ) : (
              <>
                {emiSchedules.length > 0 ? (
                  <div className="lb-emi-list">
                    {emiSchedules.map(emi => {
                      const acct = accounts.find(a => a.id === emi.account_id);
                      return (
                        <div key={emi.id} className={`lb-emi-card ${emi.is_active === false ? 'paused' : ''}`}>
                          <div className="lb-emi-card-top">
                            <div className="lb-emi-ico">🏦</div>
                            <div className="lb-emi-info">
                              <span className="lb-emi-name">{cleanEmiName(emi.description) || 'EMI'}</span>
                              <span className="lb-emi-meta">
                                ₹{fmt(emi.amount)} · {acct ? acct.name : '—'}
                                {emi.next_run_date ? ` · next ${emi.next_run_date}` : ''}
                              </span>
                            </div>
                            <div className="lb-emi-actions">
                              <button className="lb-emi-btn run" onClick={() => handleEmiRunNow(emi.id)} title="Pay this month's EMI now">
                                🚀 Pay Now
                              </button>
                              <button className="lb-emi-btn edit" onClick={() => handleEditEmi(emi)} title="Edit">✏️</button>
                              <button className="lb-emi-btn pause" onClick={() => handleEmiToggleActive(emi)} title={emi.is_active === false ? 'Resume' : 'Pause'}>
                                {emi.is_active === false ? '▶' : '⏸'}
                              </button>
                              <button className="lb-emi-btn delete" onClick={() => handleEmiDelete(emi.id)} title="Delete">🗑</button>
                            </div>
                          </div>
                          <EmiProgress emi={emi} variant="full" />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="lb-empty-state">
                    <p>No EMIs tracked yet. Add one to start watching upcoming payments.</p>
                  </div>
                )}
                <div className="lb-emi-footer">
                  {overdueEmiCount > 0 && (
                    <button
                      className="lb-catchup-btn"
                      onClick={handleCatchUp}
                      disabled={catchingUp}
                      title="Auto-record every missed EMI up to today"
                    >
                      {catchingUp ? 'Catching up…' : `🔄 Catch up ${overdueEmiCount} overdue`}
                    </button>
                  )}
                  <button className="lb-add-emi-btn" onClick={() => setShowEmiForm(true)}>
                    + Add New EMI / Loan
                  </button>
                </div>
              </>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default LendBorrow;
